import {computed, effect, Injectable, signal, Signal, WritableSignal} from '@angular/core';
import {Subject} from 'rxjs';
import {MenuItem} from 'primeng/api';

export type MenuMode = 'static' | 'overlay' | 'horizontal' | 'slim' | 'slim-plus' | 'reveal' | 'drawer';

/**
 * Ojo con el reparto de responsabilidades: aquí vive SOLO claro/oscuro y el
 * modo de menú. El color primario, la superficie y los esquemas de
 * sidebar/fondo los posee `ThemeRuntimeService` (`@/theme/theme-runtime.service`),
 * que es quien los persiste y los aplica al DOM.
 *
 * Antes esta interfaz declaraba `preset`/`primary`/`surface` heredados de la
 * plantilla Atlantis; nadie los leía, y mantenerlos junto al estado real habría
 * dejado dos sitios distintos afirmando cuál es el color primario.
 */
export interface LayoutConfig {
    darkTheme: boolean;
    menuMode: MenuMode;
}

export interface LayoutState {
    staticMenuDesktopInactive: boolean;
    overlayMenuActive: boolean;
    configSidebarVisible: boolean;
    staticMenuMobileActive: boolean;
    menuHoverActive: boolean;
    rightMenuActive: boolean;
    sidebarActive: boolean;
    activeMenuItem: any;
    overlaySubmenuActive: boolean;
    anchored: boolean;
    rightMenuVisible: boolean;
    searchBarActive: boolean;
}

export interface MenuChangeEvent {
    key: string;
    routeEvent?: boolean;
}

export interface TabCloseEvent {
    tab: MenuItem;
    index: number;
}

@Injectable({
    providedIn: 'root'
})
export class LayoutService {
    static readonly THEME_STORAGE_KEY = 'sym70.theme';

    private static resolveInitialDarkTheme(): boolean {
        const stored = localStorage.getItem(LayoutService.THEME_STORAGE_KEY);
        if (stored === 'dark') return true;
        if (stored === 'light') return false;
        return false;
    }

    _config: LayoutConfig = {
        darkTheme: LayoutService.resolveInitialDarkTheme(),
        menuMode: 'slim-plus'
    };

    _state: LayoutState = {
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        rightMenuActive: false,
        configSidebarVisible: false,
        staticMenuMobileActive: false,
        menuHoverActive: false,
        sidebarActive: false,
        anchored: false,
        activeMenuItem: null,
        overlaySubmenuActive: false,
        rightMenuVisible: false,
        searchBarActive: false
    };

    layoutConfig = signal<LayoutConfig>(this._config);

    layoutState = signal<LayoutState>(this._state);

    private configUpdate = new Subject<LayoutConfig>();

    private overlayOpen = new Subject<any>();

    private menuSource = new Subject<MenuChangeEvent>();

    private resetSource = new Subject();

    menuSource$ = this.menuSource.asObservable();

    resetSource$ = this.resetSource.asObservable();

    configUpdate$ = this.configUpdate.asObservable();

    overlayOpen$ = this.overlayOpen.asObservable();

    isSidebarActive: Signal<boolean> = computed(() => this.layoutState().overlayMenuActive || this.layoutState().staticMenuMobileActive || this.layoutState().overlaySubmenuActive);

    isSidebarStateChanged = computed(() => {
        const layoutConfig = this.layoutConfig();
        return layoutConfig.menuMode === 'horizontal' || layoutConfig.menuMode === 'slim' || layoutConfig.menuMode === 'slim-plus';
    });

    isDarkTheme: Signal<boolean> = computed(() => this.layoutConfig().darkTheme);

    isOverlay: Signal<boolean> = computed(() => this.layoutConfig().menuMode === 'overlay');

    isSlim: Signal<boolean> = computed(() => this.layoutConfig().menuMode === 'slim');

    isSlimPlus: Signal<boolean> = computed(() => this.layoutConfig().menuMode === 'slim-plus');

    isHorizontal: Signal<boolean> = computed(() => this.layoutConfig().menuMode === 'horizontal');

    transitionComplete: WritableSignal<boolean> = signal<boolean>(false);

    logo = computed(() => (this.layoutConfig().darkTheme ? 'light' : 'dark'));

    rightMenuVisible = computed(() => this.layoutState().rightMenuActive);

    private initialized = false;

    constructor() {
        effect(() => {
            const config = this.layoutConfig();
            if (config) {
                this.onConfigUpdate();
            }
            document.documentElement.style.fontSize = '11px';
        });

        effect(() => {
            const config = this.layoutConfig();

            if (!this.initialized || !config) {
                this.initialized = true;
                return;
            }

            this.handleDarkModeTransition(config);
        });

        effect(() => {
            this.isSidebarStateChanged() && this.reset();
        });
    }

    private handleDarkModeTransition(config: LayoutConfig): void {
        if ((document as any).startViewTransition) {
            this.startViewTransition(config);
        } else {
            this.toggleDarkMode(config);
            this.onTransitionEnd();
        }
    }

    private startViewTransition(config: LayoutConfig): void {
        const transition = (document as any).startViewTransition(() => {
            this.toggleDarkMode(config);
        });

        transition.ready
            .then(() => {
                this.onTransitionEnd();
            })
            .catch(() => {});
    }

    /** Flips the dark/light flag; the config effect picks it up and applies `.app-dark` to <html>. */
    toggleTheme(): void {
        this.layoutConfig.update((state) => ({
            ...state,
            darkTheme: !state.darkTheme
        }));
    }

    /**
     * Único punto donde el tema elegido se escribe en el DOM. Aplica DOS marcas
     * sobre <html>, y ambas son obligatorias:
     *
     *  1. `.app-dark`  — disparador de PrimeNG (`darkModeSelector`) y de los
     *                    alias de token en `_app-bridge.scss`.
     *  2. `data-theme` — desactiva el bloque automático por SO de tokens.css:
     *                    `@media (prefers-color-scheme: dark) { :root:not([data-theme="light"]) { … } }`
     *
     * Sin (2), en cualquier equipo con el SO/navegador en oscuro ese media query
     * hacía match SIEMPRE — también en modo claro de la app, porque el guardia
     * `:not([data-theme="light"])` nunca se cumplía (el atributo no se escribía
     * nunca). Resultado: tokens --sym-* oscuros conviviendo con el tema CLARO de
     * PrimeNG ⇒ sidebar/topbar/paneles blancos sobre contenido oscuro. El bug no
     * se reproducía en Playwright porque su Chromium arranca con
     * `prefers-color-scheme: light` (ver THEME_MIGRATION_STATUS.md).
     *
     * Escribir el atributo explícitamente en ambas ramas (no solo quitarlo) evita
     * depender del estado previo del DOM y mantiene tokens.css intacto —byte-idéntico
     * al handoff de marca en `design/`— usando su guardia tal como fue diseñado.
     */
    toggleDarkMode(config?: LayoutConfig): void {
        const _config = config || this.layoutConfig();
        const root = document.documentElement;
        if (_config.darkTheme) {
            root.classList.add('app-dark');
            root.setAttribute('data-theme', 'dark');
            localStorage.setItem(LayoutService.THEME_STORAGE_KEY, 'dark');
        } else {
            root.classList.remove('app-dark');
            root.setAttribute('data-theme', 'light');
            localStorage.setItem(LayoutService.THEME_STORAGE_KEY, 'light');
        }
    }

    private onTransitionEnd() {
        this.transitionComplete.set(true);
        setTimeout(() => {
            this.transitionComplete.set(false);
        });
    }

    onMenuToggle() {
        if (this.isOverlay()) {
            this.layoutState.update((prev) => ({
                ...prev,
                overlayMenuActive: !this.layoutState().overlayMenuActive
            }));

            if (this.layoutState().overlayMenuActive) {
                this.overlayOpen.next(null);
            }
        }

        if (this.isDesktop()) {
            this.layoutState.update((prev) => ({
                ...prev,
                staticMenuDesktopInactive: !this.layoutState().staticMenuDesktopInactive
            }));
        } else {
            this.layoutState.update((prev) => ({
                ...prev,
                staticMenuMobileActive: !this.layoutState().staticMenuMobileActive
            }));

            if (this.layoutState().staticMenuMobileActive) {
                this.overlayOpen.next(null);
            }
        }
    }

    isDesktop() {
        return window.innerWidth > 991;
    }

    isMobile() {
        return !this.isDesktop();
    }

    onConfigUpdate() {
        this._config = { ...this.layoutConfig() };
        this.configUpdate.next(this.layoutConfig());
        this.toggleDarkMode();
    }

    onMenuStateChange(event: MenuChangeEvent) {
        this.menuSource.next(event);
    }

    reset() {
        this.resetSource.next(true);
    }

    onOverlaySubmenuOpen() {
        this.overlayOpen.next(null);
    }

    showProfileSidebar() {
        this.layoutState.update((prev) => ({
            ...prev,
            profileSidebarVisible: true
        }));
    }

    showConfigSidebar() {
        this.layoutState.update((prev) => ({
            ...prev,
            configSidebarVisible: true
        }));
    }

    hideConfigSidebar() {
        this.layoutState.update((prev) => ({
            ...prev,
            configSidebarVisible: false
        }));
    }
}
